import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { getAttribution } from "../lib/tracking";
import { trackSignup, trackTrialStart, trackEvent } from "../lib/analytics";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  useEffect(() => { trackEvent("Lead"); }, []);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", mobile: "", country: "" });
  const [loading, setLoading] = useState(false);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const attr = getAttribution();
      const res = await signup({
        ...form,
        utm_source: attr.utm_source || null,
        utm_medium: attr.utm_medium || null,
        utm_campaign: attr.utm_campaign || null,
        referrer: attr.referrer || document.referrer || "",
      });
      toast.success("Account created!", { duration: 4000 });
      trackSignup({ method: "email" });
      trackTrialStart({ method: "email" });
      nav(`/verify?email=${encodeURIComponent(form.email)}`, {
        state: { devCode: res.dev_verification_code || null },
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign up failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <Nav />
      <div className="max-w-lg mx-auto px-6 py-12">
        <span className="text-xs uppercase tracking-[0.22em] text-[#2E9D96] font-semibold">Step 1 of 2</span>
        <h1 className="font-display text-4xl text-slate-800 mt-2">Say hi to Zaf.</h1>
        <p className="text-slate-600 mt-2">The basics — 30 seconds. You can add the rest after.</p>

        <form onSubmit={submit} className="mt-8 bg-white rounded-3xl p-8 border border-slate-100 shadow-sm space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First name" testid="signup-first-name">
              <input required value={form.first_name} onChange={on("first_name")} className="input zaf-focus" data-testid="signup-first-name-input" />
            </Field>
            <Field label="Last name" testid="signup-last-name">
              <input required value={form.last_name} onChange={on("last_name")} className="input zaf-focus" data-testid="signup-last-name-input" />
            </Field>
          </div>
          <Field label="Email">
            <input type="email" required value={form.email} onChange={on("email")} className="input zaf-focus" data-testid="signup-email-input" />
          </Field>
          <Field label="Password">
            <input type="password" required minLength={6} value={form.password} onChange={on("password")} className="input zaf-focus" data-testid="signup-password-input" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Mobile (optional)">
              <input value={form.mobile} onChange={on("mobile")} className="input zaf-focus" data-testid="signup-mobile-input" />
            </Field>
            <Field label="Country (optional)">
              <input value={form.country} onChange={on("country")} className="input zaf-focus" data-testid="signup-country-input" />
            </Field>
          </div>

          <button data-testid="signup-submit" disabled={loading} className="w-full bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-60 text-white font-semibold rounded-full py-3 transition-colors">
            {loading ? "Creating…" : "Create account"}
          </button>
          <p className="text-sm text-slate-500 text-center">
            Already here? <Link to="/login" className="text-[#2E9D96] font-semibold">Log in</Link>
          </p>
        </form>
      </div>
      <StyleTag />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

export function StyleTag() {
  return (
    <style>{`
      .input {
        width: 100%;
        border: 1px solid #E2E8F0;
        background: #fff;
        border-radius: 0.9rem;
        padding: 0.75rem 1rem;
        font: inherit;
        color: #2D3748;
        transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
      }
      .input::placeholder { color: #A0AEC0; }
    `}</style>
  );
}
